import { PriceStrategy } from '@snap-types/models/enums';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { ErrorMonitoring } from '@ui/services/error-monitoring';

import { OnQuantityChangeInput, OnQuantityChangeOutput } from '../types';
import { buildStrategyCalculations } from './build-strategy-calculations';

type QuantityChangeFallbackParams = {
  currentStrategy: PriceStrategy;
  priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  inputs: OnQuantityChangeInput;
};

type QuantityChangeFallbackResult = OnQuantityChangeOutput & {
  strategy?: PriceStrategy;
};

export function handleQuantityChangeFallback({
  currentStrategy,
  priceStrategyConfigs,
  inputs,
}: QuantityChangeFallbackParams): QuantityChangeFallbackResult {
  let currentInputs = inputs;

  const startingConfigIndex = priceStrategyConfigs.findIndex(
    (config) => config.priceStrategyType === currentStrategy,
  );

  const results: QuantityChangeFallbackResult[] = [];

  if (startingConfigIndex === -1) {
    // Strategy not found in configs, return failure
    return {
      status: 'failure',
      reason: `Strategy ${currentStrategy} not found in price strategy configs`,
    };
  }

  for (let i = startingConfigIndex; i < priceStrategyConfigs.length; i++) {
    const config = priceStrategyConfigs[i];

    // Set the strategy in the inputs for the next calculation
    currentInputs = {
      ...currentInputs,
      quoteLine: {
        ...currentInputs.quoteLine,

        // @HACK(NT): Prevent the fallback from using the last win to populate missing values
        // @SEE: https://github.com/mwcomponents/quickquote/issues/2990#issuecomment-3528974807
        lastWin: {
          ...currentInputs.quoteLine.lastWin,
          hasLastWin: false,
        },
      },
      quotePrice: {
        ...currentInputs.quotePrice,
        priceStrategy: config.priceStrategyType,
      },
    };

    const calculation =
      buildStrategyCalculations('onQuantityChange')[config.priceStrategyType];

    if (!calculation) {
      return {
        status: 'failure',
        reason: `No calculation function found for strategy ${config.priceStrategyType}`,
      };
    }

    const result = calculation(currentInputs);

    if (result.status === 'success' || result.status === 'no-op') {
      return {
        ...result,
        strategy: config.priceStrategyType,
      };
    }

    results.push(result);
  }

  // If everything has failed so far, try the original config without fallback
  const originalConfig = priceStrategyConfigs[startingConfigIndex];
  const originalConfigWithoutFallback = {
    ...originalConfig,
    allowFallback: false,
  };

  const calculation =
    buildStrategyCalculations('onQuantityChange')[
      originalConfig.priceStrategyType
    ];

  if (!calculation) {
    return {
      status: 'failure',
      reason: `No calculation function found for strategy ${originalConfig.priceStrategyType}`,
    };
  }

  const result = calculation({
    ...inputs,
    priceStrategyConfigs: [originalConfigWithoutFallback],
  });

  if (result.status === 'success') {
    return {
      ...result,
      status: 'fallback-failure',
      reason: 'Maximum quantity quantity break exceeded',
      strategy: originalConfigWithoutFallback.priceStrategyType,
    };
  }

  ErrorMonitoring.captureException(
    new Error(`Could not calculate price for strategy ${currentStrategy}`),
    {
      extra: {
        results,
      },
    },
  );

  return {
    status: 'failure',
    reason: `Could not calculate price for strategy ${currentStrategy}`,
  };
}
